import { SerializedError, createSlice, isAnyOf } from '@reduxjs/toolkit';

import { ApiQueries, REQUEST_METHODS } from '@lib/core/service/consts';
import { createTypedAsyncThunk } from '@lib/core/service/createTypedAsyncThunk';
import { parseError } from '@lib/core/service/requests/parseError';
import { request } from '@lib/core/service/requests/request';
import { selectLocationListData } from '@lib/core/users/selectors/locationList';
import { locationListApiUrlCreator } from '@lib/core/users/slices/urls';
import { ILocation, IUpdateLocationListParams } from '@lib/core/users/types';

export interface ILocationListState {
  data: ILocation[];
  error: SerializedError;
  isLoading: boolean;
}

export const initialState: ILocationListState = {
  data: [],
  error: null,
  isLoading: false,
};

export const actionGetLocationList = createTypedAsyncThunk('users/locationList/actionGetLocationList', async () =>
  request({
    url: locationListApiUrlCreator(),
  }),
);

export const actionUpdateLocationList = createTypedAsyncThunk(
  'users/locationList/actionUpdateLocationList',
  async ({ retailerLocationSlug }: IUpdateLocationListParams, { getState }) => {
    try {
      const state = getState();
      const locationListData = selectLocationListData(state);

      const isLocationInList = !!locationListData.find(
        location => location.retailer_location.slug === retailerLocationSlug,
      );

      const body = {
        ...(!isLocationInList && { [ApiQueries.retailerLocation]: retailerLocationSlug }),
      };

      return await request({
        body,
        method: isLocationInList ? REQUEST_METHODS.DELETE : REQUEST_METHODS.POST,
        url: locationListApiUrlCreator(isLocationInList && retailerLocationSlug),
      }).then(() =>
        request({
          url: locationListApiUrlCreator(),
        }),
      );
    } catch (error) {
      return Promise.reject(parseError(error));
    }
  },
);

const locationListSlice = createSlice({
  extraReducers: builder => {
    builder.addMatcher(isAnyOf(actionGetLocationList.pending, actionUpdateLocationList.pending), state => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addMatcher(
      isAnyOf(actionGetLocationList.fulfilled, actionUpdateLocationList.fulfilled),
      (state, action) => {
        state.isLoading = false;
        state.data = action.payload.results;
      },
    );
    builder.addMatcher(isAnyOf(actionGetLocationList.rejected, actionUpdateLocationList.rejected), (state, action) => {
      state.error = action.error;
      state.isLoading = false;
    });
  },
  initialState,
  name: 'locationList',
  reducers: {
    actionResetLocationList: () => initialState,
  },
});

export const { actionResetLocationList } = locationListSlice.actions;
export default locationListSlice.reducer;
